import { Component, Host, h, Element, Prop, Listen, Method, Watch, Event, EventEmitter } from "@stencil/core";

import { StzhTimelineItemOpenEvent, StzhTimelineFoldableChangeEvent } from "../../index";

// import { parents } from "../../utils/utils";

/**
 * @slot - Slot for `stzh-timeline-item` elements
 */
@Component({
  tag: "stzh-timeline",
  styleUrl: "stzh-timeline.scss",
  shadow: true,
})
export class StzhTimeline {
  /** Whether folds are foldable */
  @Prop() foldable: boolean = false;

  /** Whether folds are collapsible */
  @Prop() collapsible: boolean = true;

  /** Whether multiple folds can be opened at once */
  @Prop() multiselectable: boolean = false;

  @Element() element: HTMLStzhTimelineElement;

  /** Foldable change event */
  @Event() stzhFoldableChange: EventEmitter<StzhTimelineFoldableChangeEvent>;

  @Watch("foldable")
  foldableWatcher(newValue: boolean) {
    this.stzhFoldableChange.emit({
      component: "stzh-timeline",
      value: newValue,
    });
  }

  @Listen("stzhOpen")
  openListener(event: CustomEvent<StzhTimelineItemOpenEvent>) {
    const timelineItemOpened = event.target as HTMLStzhTimelineItemElement;
    const timelineOpened = timelineItemOpened.closest("stzh-timeline");

    if (
      this.multiselectable ||
      (event.detail && event.detail.component !== "stzh-timeline-item") ||
      timelineOpened !== this.element
    ) {
      return;
    }

    const items: HTMLStzhTimelineItemElement[] = Array.from(timelineOpened.querySelectorAll("stzh-timeline-item"));
    items.forEach(item => {
      if (item !== timelineItemOpened) {
        item.hide();
      }
    });
  }

  /** Set focus to timeline item */
  @Method()
  async setItemFocus(timelineItem: HTMLStzhTimelineItemElement, action: string) {
    const timeline = timelineItem.closest("stzh-timeline");

    if (timeline !== this.element) {
      return;
    }

    const items = (
      Array.from(timeline.querySelectorAll(":scope > stzh-timeline-item")) as HTMLStzhTimelineItemElement[]
    ).filter(item => !item.disabled);

    const currentIndex = items.indexOf(timelineItem);

    if ((action === "prev" || action === "next") && currentIndex === null) {
      action = action === "prev" ? "last" : "first";
    }

    if (action === "prev" && currentIndex === 0) {
      action = "last";
    }

    if (action === "next" && currentIndex === items.length - 1) {
      action = "first";
    }

    if (action === "prev") {
      items[currentIndex - 1].focus();
    } else if (action === "next") {
      items[currentIndex + 1].focus();
    } else if (action === "last") {
      items[items.length - 1].focus();
    } else if (action === "first") {
      items[0].focus();
    } else {
      timelineItem.focus();
    }
  }

  @Method()
  async updateSteps() {
    const host = (this.element.getRootNode() as ShadowRoot)?.host || this.element;

    // const parentTimelinesCount = parents(this.element)
    //   .filter((element) => element.nodeName.toLowerCase() === 'stzh-timeline')
    //   .length;

    const parentTimelineItem = host.closest("stzh-timeline-item");
    let parentIndex = -1;

    if (parentTimelineItem) {
      parentIndex = Array.from(parentTimelineItem.parentNode.children).indexOf(parentTimelineItem);
    }

    const items = Array.from(host.querySelectorAll(":scope > stzh-timeline-item")) as HTMLStzhTimelineItemElement[];
    items.forEach((item, index) => {
      item.step = parentIndex > -1 ? parentIndex + 1 + (index + 1) / 10 : index + 1;

      const nestedTimeline = item.querySelector("stzh-timeline");

      if (nestedTimeline) {
        nestedTimeline.updateSteps();
      }
    });
  }

  private handleSlotchange = () => {
    this.updateSteps();
  };

  render() {
    const classes = {
      "stzh-timeline": true,
    };

    return (
      <Host>
        <div class={classes} role="list">
          <slot onSlotchange={this.handleSlotchange}></slot>
        </div>
      </Host>
    );
  }
}
